import React from 'react';
import { BasicForm, TextAreaFieldItem, ITreeSelectProps, notification, ITreeSelectDataSource, Spinner, Typography } from 'ui-lib';
import { CheckboxGroupItem, TextFieldItem, IconTextButton, useForm, } from 'ui-lib';
import fileTransferCss from './new-file-transfer.css';
import { useFileTransfersStore, useStores } from 'store';
import { confirmAction } from 'pages/private/admin/utils';
import { PERMISSIONS_OPTIONS } from './transfer-files-tab';
import { UploadFileDto } from 'dto/upload-file.dto';
import { DeviceGroupSelectItem } from 'pages/private/components/device-groups-select';
import { getErrorMessage, isNullOrEmpty } from 'common-utils';
import { useUrlSearch } from 'hooks/use-search.hook';

interface ICreateTransferFileProps {
    deviceGroupsDataSource: ITreeSelectDataSource[];
    deviceGroupLoading: boolean;
    selectedClientAccountId: number;
    uploadFile: UploadFileDto;
    onCancel?: () => void;
}

export const CreateTransferFile = (props: ICreateTransferFileProps) => {
    const stores = useStores();
    const fileTransfersStore = useFileTransfersStore();
    const [form] = useForm();
    const { getSearchParam, updateSearchParams } = useUrlSearch();
    const [permissionsCheckedList, setPermissionsCheckedList] = React.useState([4]);
    const [destination, setFolderPath] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [selectedDeviceGroup, setSelectedDeviceGroup] = React.useState<string>('');

    React.useEffect(() => {
        setSelectedDeviceGroup(() => {
            const paramDeviceGroup = getSearchParam('deviceGroup') || '';
            return props.deviceGroupsDataSource?.find(dg => dg.treeDataNode.value === paramDeviceGroup) ? paramDeviceGroup : '';
        });
    }, [props.deviceGroupsDataSource]);

    const deviceGroupsSelectProps: ITreeSelectProps = {
        dataSource: props.deviceGroupsDataSource,
        expandAll: true,
        allowClear: true,
        onChange: (value) => {
            setSelectedDeviceGroup(value || '');
            updateSearchParams({ deviceGroup: value || null });
        },
    };
    const createTransferFile = async (values: any) => {
        try {
            const permissionsCode = permissionsCheckedList.reduce((p: number, c) => p + Number(c), 0);
            const permissionsCodeLinux = `${permissionsCode}${permissionsCode}${permissionsCode}`;

            const result = await fileTransfersStore.createFileTransfer(
                props.selectedClientAccountId,
                selectedDeviceGroup,
                {
                    destinationPath: destination,
                    executeCommand: isNullOrEmpty(values.shellCommandField) ? '' : values.shellCommandField,
                    permissions: permissionsCodeLinux,
                    description: description,
                    uploadedFileUuid: props.uploadFile.uuid,
                }
            );

            if (result) {
                notification.success({
                    message: 'File Transfer Successfully Created',
                    description: `File transfer ${result.fileName} has been created.`,
                });
                props.onCancel();
            }
        } catch (err) {
            notification.error({
                message: getErrorMessage(err),
                placement: 'topRight',
            });
        }
    };

    const handleSubmit = (values: any) => {
        confirmAction(values, 'Create Transfer File', 'Do you want to create this file?', createTransferFile);
    };

    return (
        <React.Fragment>
            <Typography.Title level={2}>Create Transfer File</Typography.Title>
            <Spinner spinning={props.deviceGroupLoading} className={fileTransferCss.spinner}>
                <BasicForm
                    title='Create Transfer File'
                    form={form}
                    onSubmit={handleSubmit}
                    className={fileTransferCss['transfer-form']}
                    items={[
                        <DeviceGroupSelectItem
                            key='deviceGroup'
                            code='deviceGroup'
                            label='Device group'
                            colon={true}
                            isRequired={true}
                            labelAlign='right'
                            initialValue={selectedDeviceGroup}
                            selectProps={deviceGroupsSelectProps}
                        />,
                        <TextFieldItem
                            initialValue={props.uploadFile.fileName}
                            key='fileName'
                            code='fileName'
                            label='File name'
                            colon={true}
                            disabled={true}
                            labelAlign='right'
                        />,
                        <TextFieldItem
                            initialValue={destination}
                            key='folderPath'
                            code='folderPath'
                            label='Folder path'
                            colon={true}
                            isRequired={true}
                            labelAlign='right'
                            onChange={(value) => setFolderPath(value.target.value)}
                        />,
                        <TextAreaFieldItem
                            labelColSpan={5}
                            code='shellCommandField'
                            label='Execution script'
                            colon={true}
                            labelAlign='right'
                            isRequired={false}
                            textAreaProps={{
                                placeholder: 'Here you can specify a script or command which is executed when file is downloaded. Ex.\n#!/bin/bash\nsrh -fw scr-config',
                                autoSize: { minRows: 10, maxRows: 200 },
                            }}
                        />,
                        <TextFieldItem
                            initialValue={description}
                            key='description'
                            code='description'
                            label='Description'
                            colon={true}
                            isRequired={false}
                            labelAlign='right'
                            onChange={(value) => setDescription(value.target.value)}
                        />,
                        <CheckboxGroupItem
                            labelAlign='right'
                            key='permissions'
                            code='permissions'
                            defaultValue={permissionsCheckedList}
                            label='Permissions'
                            colon={true}
                            options={PERMISSIONS_OPTIONS}
                            value={permissionsCheckedList}
                            onChange={(value) => setPermissionsCheckedList(value)}
                        />,
                        <div className={fileTransferCss['row']}>
                            <IconTextButton
                                key='back-button'
                                className={fileTransferCss['footer-left-button']}
                                htmlType='button'
                                label='Back'
                                onClick={props.onCancel}
                            />
                            <IconTextButton
                                key='create-submit-button'
                                className={fileTransferCss['footer-right-button']}
                                htmlType='submit'
                                label='Create'
                                type='primary'
                            />
                        </div>,
                    ]}
                />
            </Spinner>
        </React.Fragment>
    );
};
